import React, { useContext, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { mdiImagePlusOutline, mdiTrashCanOutline } from "@mdi/js";

import ixusUtils from "../../../../../../script/utils/ixusUtils";
import safetyDistanceUtils from "../../../../../../script/utils/safetyDistanceUtils";
import cloud from "../../../../../../asset/ixus/cloud.png";
import dish from "../../../../../../asset/ixus/dish.png";

import { IxusFreeTextDialog } from "./IxusFreeTextDialog";

import { Grid } from "../../../../../components/Grid";
import ixusRest from "../../../../../../rest/ixusRest";
import { useInput } from "../../../../../hooks/useInput";
import { Spacer } from "../../../../../components/Spacer";
import { Button } from "../../../../../components/Button";
import { Checkbox } from "../../../../../components/Checkbox";
import { useOnChange } from "../../../../../hooks/useOnChange";
import { EntryContext } from "../../../../../contexts/EntryContext";
import { SelectSingle } from "../../../../../components/SelectSingle";
import { SelectMultiple } from "../../../../../components/SelectMultiple";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

const ImagePreviewStyle = styled.img`
    max-width: 150px;
    max-height: 150px;
    margin-top: 8px;
    border-radius: 4px;
    object-fit: contain;
`;

const SiteTypes = Object.freeze({
    ROOFTOP: "ROOFTOP",
    ROOFTOP_AERIAL_WORK_PLATFORM: "ROOFTOP_AERIAL_WORK_PLATFORM",
    PYLON_NOT_DFMG: "PYLON_NOT_DFMG",
    PYLON_DFMG_LE_60M: "PYLON_DFMG_LE_60M",
    PYLON_DFMG_LE_60M_AERIAL_WORK_PLATFORM: "PYLON_DFMG_LE_60M_AERIAL_WORK_PLATFORM",
    PYLON_DFMG_GT_60M: "PYLON_DFMG_GT_60M",
    MAJOR_SITE: "MAJOR_SITE",
});

const AntennaAccess = Object.freeze({
    ROOF_SURFACE: "ROOF_SURFACE",
    GRP_CYLINDER: "GRP_CYLINDER",
    CLIMBING_LADDER: "CLIMBING_LADDER",
    AERIAL_WORK_PLATFORM: "AERIAL_WORK_PLATFORM",
});

const AntennaAccessOptions = Object.freeze({
    [SiteTypes.ROOFTOP]: [AntennaAccess.ROOF_SURFACE, AntennaAccess.GRP_CYLINDER, AntennaAccess.CLIMBING_LADDER],
    [SiteTypes.PYLON_NOT_DFMG]: [AntennaAccess.CLIMBING_LADDER, AntennaAccess.AERIAL_WORK_PLATFORM],
});

const OtherOperators = Object.freeze({
    TELEFONICA: "TELEFONICA",
    VODAFONE: "VODAFONE",
    _1UND1: "_1UND1",
});

const OtherAntennas = Object.freeze({
    DIRECTIONAL_RADIO: "DIRECTIONAL_RADIO",
    RECEIVING_ANTENNAS: "RECEIVING_ANTENNAS",
    ANTENNAS_OUT_OF_SERVICE: "ANTENNAS_OUT_OF_SERVICE",
    ANTENNAS_WITH_SAFETY_DISTANCE_BELOW_0_5M: "ANTENNAS_WITH_SAFETY_DISTANCE_BELOW_0_5M",
});

// Matches the full backend text (IxusReportService.java TEXT_* constants, see i18n.js
// entry.ixus.textFields.fullText) to a short-label i18n key for the text-field palette buttons
// (UI space is limited). The full wording is still what gets placed on the plan/report -
// only the button caption is shortened.
const TEXT_LABEL_KEYS = [
    "roofSurfaceClear", "roofSurfaceNotClear", "aerialWorkPlatform", "aerialWorkPlatformRoofAccessNotClear",
    "climbingLadderClear", "climbingLadderRoofAccessNotClear", "climbingPathNotClear", "thirdPartySite",
    "operatorsConsidered", "operatorsNotConsidered", "operatorsOwnMarking", "safetyDistanceBelow05m",
];

const shortenButtonLabel = text => {
    const labelKey = TEXT_LABEL_KEYS.find(key => intl.get("entry.ixus.textFields.fullText." + key) === text);
    if (labelKey) {
        return intl.get("entry.ixus.textFields.shortLabel." + labelKey);
    }
    const [prefix, suffix] = intl.get("entry.ixus.textFields.fullText.operatorMarking").split("{operators}");
    if (text.startsWith(prefix) && text.endsWith(suffix)) {
        const operators = text.slice(prefix.length, text.length - suffix.length);
        return intl.get("entry.ixus.textFields.shortLabel.operatorMarking", { operators });
    }
    return text.length <= 60 ? text : text.slice(0, 57) + "...";
};

export const IxusSiteInfoLayer = props => {
    const { show, plot, selectedIndex } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { ixusSetting, plans } = entry;
    const ixusAreas = ixusSetting?.ixusAreas || [];

    const siteTypeInput = useInput("siteType", ixusSetting?.siteType);
    const antennaAccessInput = useInput("antennaAccess", ixusSetting?.antennaAccess);
    const climbingPathClearInput = useInput("climbingPathClear", ixusSetting?.climbingPathClear);
    const roofAccessClearInput = useInput("roofAccessClear", ixusSetting?.roofAccessClear);
    const otherOperatorsInput = useInput("otherOperators", ixusSetting?.otherOperators ?? []);
    const otherOperatorsConsideredInput = useInput("otherOperatorsConsidered", ixusSetting?.otherOperatorsConsidered);
    const otherAntennasInput = useInput("otherAntennas", ixusSetting?.otherAntennas ?? []);

    const [image, setImage] = useState();
    const fileInputRef = useRef(null);

    useOnChange((name, value) => {
        ixusSetting[name] = value;
        if (name === "otherOperators" && value.length === 0) {
            otherOperatorsConsideredInput.set(false, true);
            ixusSetting.otherOperatorsConsidered = false;
        }
    }, [siteTypeInput, antennaAccessInput, climbingPathClearInput, roofAccessClearInput,
        otherOperatorsInput, otherOperatorsConsideredInput, otherAntennasInput]);

    const showAntennaAccess = siteTypeInput.value === SiteTypes.ROOFTOP
        || siteTypeInput.value === SiteTypes.PYLON_NOT_DFMG;
    const showClimbingPathClear = (showAntennaAccess && antennaAccessInput.value === AntennaAccess.CLIMBING_LADDER)
        || siteTypeInput.value === SiteTypes.PYLON_DFMG_LE_60M;
    const showRoofAccessClear = siteTypeInput.value === SiteTypes.ROOFTOP_AERIAL_WORK_PLATFORM
        || (siteTypeInput.value === SiteTypes.ROOFTOP && antennaAccessInput.value === AntennaAccess.ROOF_SURFACE)
        || (siteTypeInput.value === SiteTypes.ROOFTOP && antennaAccessInput.value === AntennaAccess.CLIMBING_LADDER
            && climbingPathClearInput.value === true);

    const roofAccessClearTitle = siteTypeInput.value === SiteTypes.ROOFTOP
            && antennaAccessInput.value === AntennaAccess.ROOF_SURFACE
        ? intl.get("entry.ixus.siteInfo.roofAccessClearInput.title")
        : intl.get("entry.ixus.siteInfo.roofAccessClearInput.titleDangerZone");

    const loadLocalImage = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = e => {
        const [file] = e.target.files;
        if (!file) {
            return;
        };
        const reader = new FileReader();
        reader.onload = event => {
            setImage(event.target.result);
            ixusSetting.reportImage = event.target.result;
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const loadReportImage = async () => {
        try {
            const image = await ixusRest.getReportImage(entry.id);
            setImage(image);
        } catch {
            console.error("No report image uploaded yet for entry", entry.id);
        }
    };

    useEffect(() => {
        if(show && !image) {
            loadReportImage();
        }
    },[show, image]);

    const textFieldsLayerName = "ixusTextFieldsLayer";

    const [availableTexts, setAvailableTexts] = useState([]);
    const [armedText, setArmedText] = useState();
    const [drawingArrow, setDrawingArrow] = useState(false);
    const [selectedTextbox, setSelectedTextbox] = useState();
    const [freeTextOpen, setFreeTextOpen] = useState(false);
    const arrowCancelRef = useRef(null);

    const selectedIxusArea = selectedIndex !== undefined ? ixusAreas[selectedIndex] : undefined;
    const selectedPlan = plans.find(plan => plan.key === selectedIxusArea?.planKey);

    const placedTextFields = selectedIxusArea?.calculation?.ixusResult?.textFields || [];
    // Plain text boxes carry no kind at all - the placed symbols (table, cloud, directional
    // radio, climbing square) all do, so this excludes them from the predefined-text list.
    const placedTexts = placedTextFields
        .filter(textField => !textField.kind)
        .map(textField => textField.text);
    const unplacedTexts = availableTexts.filter(text => !placedTexts.includes(text));

    // Matches the backend text placed when "Richtfunk" is picked as another antenna at the
    // site (IxusReportService.java TEXT_DIRECTIONAL_RADIO) - clicking that palette button
    // places the dish icon alongside it instead of as plain text (service-desk#1663).
    const directionalRadioText = intl.get("entry.ixus.textFields.fullText.directionalRadio");

    // A top view carries the table of its own level, a side view one table per level.
    const levels = safetyDistanceUtils.getLevels(ixusAreas, plans);
    const placedLevelKeys = placedTextFields
        .filter(textField => textField.kind === safetyDistanceUtils.kind)
        .map(textField => textField.levelPlanKey);
    const unplacedLevels = levels
        .filter(level => selectedIxusArea?.sideView || level.planKey === selectedIxusArea?.planKey)
        .filter(level => !placedLevelKeys.includes(level.planKey));

    const selectedIndexRef = useRef(selectedIndex);
    useEffect(() => {
        selectedIndexRef.current = selectedIndex;
    }, [selectedIndex]);

    useEffect(() => {
        if (show) {
            ixusRest.getTextFieldOptions(ixusSetting).then(setAvailableTexts);
        }
    }, [show, siteTypeInput.value, antennaAccessInput.value, climbingPathClearInput.value,
        roofAccessClearInput.value, JSON.stringify(otherOperatorsInput.value),
        otherOperatorsConsideredInput.value, JSON.stringify(otherAntennasInput.value)]);

    const saveTextFields = () => {
        const layer = plot.getLayer(textFieldsLayerName);
        const scale = plot.refScale;
        const textFields = [];
        layer.getObjects().forEach(obj => {
            if (obj.feature !== "placement-feature") {
                return;
            }
            const placement = {
                left: obj.left * scale,
                top: obj.top * scale,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
                angle: obj.angle,
            };
            // The table box is rebuilt from the current measurements on every draw, and the
            // symbols (cloud, directional radio, climbing square) have a fixed look - all of
            // them only need their placement stored.
            if (obj.kind === safetyDistanceUtils.kind) {
                textFields.push({ kind: obj.kind, levelPlanKey: obj.levelPlanKey, ...placement });
                return;
            }
            if (obj.kind === safetyDistanceUtils.arrowKind || obj.kind === safetyDistanceUtils.redLineKind) {
                // Arrows and red lines keep their shape (drawn freely, not a fixed icon), so the current
                // endpoints are read out via the transform matrix instead of left/top/scale/angle.
                const [line] = obj.getObjects();
                const [p1, p2] = plot.getLineEndpoints(line);
                textFields.push({
                    kind: obj.kind,
                    points: [{ x: p1.x * scale, y: p1.y * scale }, { x: p2.x * scale, y: p2.y * scale }],
                });
                return;
            }
            if (obj.kind) {
                textFields.push({ kind: obj.kind, ...placement });
                return;
            }
            textFields.push({
                text: obj.textboxRef.text,
                width: obj.textboxRef.width * scale,
                ...placement,
            });
        });
        const ixusArea = selectedIndexRef.current !== undefined
            ? ixusAreas[selectedIndexRef.current]
            : undefined;
        if (ixusArea?.calculation?.ixusResult) {
            ixusArea.calculation.ixusResult.textFields = textFields;
        }
    };

    // Also used to switch back from arrow-drawing mode (applyLineFeature) to the normal
    // single-click placement mode once an arrow is placed or cancelled (service-desk#1668).
    const installPlacementFeature = () => {
        const layer = plot.getLayer(textFieldsLayerName);
        const feature = plot.applyPlacementFeature(layer, "blue");
        feature.onSelect(textbox => setSelectedTextbox(textbox));
        feature.onModify(() => saveTextFields());
        feature.onPlace(() => {
            setArmedText(undefined);
            saveTextFields();
        });
    };

    useEffect(() => {
        plot.addLayer(textFieldsLayerName, 140);
        installPlacementFeature();
    }, []);

    const drawTextFields = plan => {
        const layer = plot.getLayer(textFieldsLayerName);
        return ixusUtils.drawTextFields(plan, plot, layer, ixusAreas, plans, siteTypeInput.value);
    };

    // drawTextFields loads images (warning triangle, cloud, dish) asynchronously. Without
    // this chain, a fast siteType toggle could start a second redraw before the first one's
    // addObject calls have all landed, clearing the layer out from under it and leaving
    // orphaned duplicates behind. Chaining onto the previous run guarantees every redraw's
    // objects are fully in place (or the layer is untouched by it) before the next one clears.
    const redrawChainRef = useRef(Promise.resolve());
    useEffect(() => {
        redrawChainRef.current = redrawChainRef.current.catch(() => undefined).then(async () => {
            if (arrowCancelRef.current) {
                arrowCancelRef.current();
                arrowCancelRef.current = null;
                setDrawingArrow(false);
            }
            const layer = plot.getLayer(textFieldsLayerName);
            layer.removeObjects();
            if (show && selectedPlan) {
                layer.activate();
                await drawTextFields(selectedPlan);
            } else {
                setArmedText(undefined);
            }
        });
    }, [show, siteTypeInput.value]);

    const armText = text => {
        plot.getLayer(textFieldsLayerName).feature.arm(text);
        setArmedText(text);
    };

    const armSafetyDistances = level => {
        const levelArea = ixusAreas.find(ixusArea => ixusArea.planKey === level.planKey);
        const content = safetyDistanceUtils.buildContent(level.planKey, ixusAreas, levelArea?.distanceNote, siteTypeInput.value);
        plot.getLayer(textFieldsLayerName).feature.arm({
            content,
            properties: { kind: safetyDistanceUtils.kind, levelPlanKey: level.planKey },
        });
        setArmedText(level.label);
    };

    const armCloud = () => {
        plot.getLayer(textFieldsLayerName).feature.arm({
            image: cloud,
            opacity: safetyDistanceUtils.cloudOpacity,
            properties: { kind: safetyDistanceUtils.cloudKind },
        });
        setArmedText(intl.get("entry.ixus.textFields.cloud"));
    };

    const armDirectionalRadio = () => {
        plot.getLayer(textFieldsLayerName).feature.arm({
            icon: dish,
            text: intl.get("entry.ixus.textFields.directionalRadioCaption"),
            properties: { kind: safetyDistanceUtils.directionalRadioKind },
        });
        setArmedText(intl.get("entry.ixus.textFields.directionalRadio"));
    };

    const armClimbingSquare = () => {
        plot.getLayer(textFieldsLayerName).feature.arm({
            size: safetyDistanceUtils.climbingSquareSizeMeters / plot.refScale,
            properties: { kind: safetyDistanceUtils.climbingSquareKind, ...ixusUtils.climbingSquareStyle },
        });
        setArmedText(intl.get("entry.ixus.textFields.climbingSquare"));
    };

    // Arrows and red lines are drawn freehand (start point, end point) instead of placed with one
    // click, so this temporarily swaps the layer over to applyLineFeature and back
    // (service-desk#1668, #1694).
    const armLine = (kind, color, useArrows, label) => {
        const layer = plot.getLayer(textFieldsLayerName);
        const objectsBefore = new Set(layer.getObjects());
        // Placed symbols must not swallow the clicks while drawing, otherwise a line cannot
        // start on top of an existing one (service-desk#1668).
        const eventedBefore = new Map([...objectsBefore].map(object => [object, object.evented]));
        const restoreEvented = () => eventedBefore.forEach((evented, object) => object.set({ evented }));
        objectsBefore.forEach(object => object.set({ evented: false }));
        const feature = plot.applyLineFeature(layer, color, true, useArrows);
        feature.onPlace(group => {
            // hasControls defaults to false (plot.js FabricObject.ownDefaults), unlike the
            // image-based symbols which turn it on explicitly - arrows need it too so they can
            // be moved/rotated/resized after placement, not just drawn once (service-desk#1668).
            group.set({ kind, feature: "placement-feature", hasControls: true, perPixelTargetFind: true });
            restoreEvented();
            installPlacementFeature();
            setSelectedTextbox(group);
            arrowCancelRef.current = null;
            setDrawingArrow(false);
            setArmedText(undefined);
            saveTextFields();
        });
        arrowCancelRef.current = () => {
            layer.getObjects().filter(object => !objectsBefore.has(object)).forEach(object => layer.removeObject(object));
            restoreEvented();
            installPlacementFeature();
        };
        setDrawingArrow(true);
        setArmedText(label);
    };

    const armArrow = () => armLine(safetyDistanceUtils.arrowKind, "blue", true, intl.get("entry.ixus.textFields.arrow"));

    const armRedLine = () => armLine(safetyDistanceUtils.redLineKind, "red", false, intl.get("entry.ixus.textFields.redLine"));

    const cancelArm = () => {
        if (arrowCancelRef.current) {
            arrowCancelRef.current();
            arrowCancelRef.current = null;
            setDrawingArrow(false);
        } else {
            plot.getLayer(textFieldsLayerName).feature.disarm();
        }
        setArmedText(undefined);
    };

    const onDelete = () => {
        const layer = plot.getLayer(textFieldsLayerName);
        layer.removeObject(selectedTextbox);
        setSelectedTextbox(undefined);
        setTimeout(() => saveTextFields(), 0);
    };

    useEffect(() => {
        const handleKeyDown = e => {
            if (!selectedTextbox) {
                return;
            }
            if (e.key === "Delete" || e.key === "Backspace") {
                onDelete();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [selectedTextbox]);

    return (
        <LayerStyle $show={show}>
            <Grid columns="1">
                <Button
                    svgIcon={mdiImagePlusOutline}
                    value={intl.get("entry.ixus.siteInfo.selectImage")}
                    onClick={() => loadLocalImage()} />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    style={{ display: "none" }}
                    onChange={handleFileChange} />
                {image && <ImagePreviewStyle src={image} alt="Site" />}
            </Grid>
            <Spacer variant="horizontal" />
            <Grid columns="1">
                <SelectSingle {...siteTypeInput.bind}
                    title={intl.get("entry.ixus.siteInfo.siteTypeInput.title")}
                    toLabel={value => (value ? intl.get("enum.ixusSiteType." + value) : "")}>
                    {Object.keys(SiteTypes).map(key =>
                        <option key={key} value={SiteTypes[key]} >{intl.get("enum.ixusSiteType." + SiteTypes[key])}</option>
                    )}
                </SelectSingle>
                {showAntennaAccess &&
                    <SelectSingle {...antennaAccessInput.bind}
                        title={intl.get("entry.ixus.siteInfo.antennaAccessInput.title")}
                        toLabel={value => (value ? intl.get("enum.ixusAntennaAccess." + value) : "")}>
                        {AntennaAccessOptions[siteTypeInput.value].map(value =>
                            <option key={value} value={value} >{intl.get("enum.ixusAntennaAccess." + value)}</option>
                        )}
                    </SelectSingle>
                }
                {showClimbingPathClear &&
                    <Checkbox {...climbingPathClearInput.bind} title={intl.get("entry.ixus.siteInfo.climbingPathClearInput.title")} />
                }
                {showRoofAccessClear &&
                    <Checkbox {...roofAccessClearInput.bind} title={roofAccessClearTitle} />
                }
                <SelectMultiple {...otherOperatorsInput.bind}
                    title={intl.get("entry.ixus.siteInfo.otherOperatorsInput.title")}
                    toLabel={value => intl.get("enum.ixusOtherOperator." + value)}>
                    {Object.keys(OtherOperators).map(key =>
                        <option key={key} value={OtherOperators[key]} >{intl.get("enum.ixusOtherOperator." + OtherOperators[key])}</option>
                    )}
                </SelectMultiple>
                <Checkbox {...otherOperatorsConsideredInput.bind}
                    title={intl.get("entry.ixus.siteInfo.otherOperatorsConsideredInput.title")}
                    disabled={otherOperatorsInput.value.length === 0} />
                <SelectMultiple {...otherAntennasInput.bind}
                    title={intl.get("entry.ixus.siteInfo.otherAntennasInput.title")}
                    toLabel={value => intl.get("enum.ixusOtherAntenna." + value)}>
                    {Object.keys(OtherAntennas).map(key =>
                        <option key={key} value={OtherAntennas[key]} >{intl.get("enum.ixusOtherAntenna." + OtherAntennas[key])}</option>
                    )}
                </SelectMultiple>
            </Grid>
            {selectedIndex !== undefined &&
                <>
                    <Spacer variant="horizontal" />
                    <Grid columns="1">
                        {armedText
                            ? <>
                                <div>{intl.get(drawingArrow
                                    ? "entry.ixus.textFields.arrowPlaceHint"
                                    : "entry.ixus.textFields.placeHint")}</div>
                                <Button
                                    onClick={() => cancelArm()}
                                    value={intl.get("common.cancel")} />
                            </>
                            : <>
                                <Button
                                    svgIcon={mdiTrashCanOutline}
                                    disabled={!selectedTextbox}
                                    onClick={() => onDelete()}
                                    value={intl.get("common.delete")} />
                                <Button
                                    onClick={() => setFreeTextOpen(true)}
                                    value={intl.get("entry.ixus.textFields.freeText")} />
                                <Button
                                    onClick={() => armCloud()}
                                    value={intl.get("entry.ixus.textFields.cloud")} />
                                <Button
                                    onClick={() => armArrow()}
                                    value={intl.get("entry.ixus.textFields.arrow")} />
                                <Button
                                    onClick={() => armRedLine()}
                                    value={intl.get("entry.ixus.textFields.redLine")} />
                                <Button
                                    onClick={() => armClimbingSquare()}
                                    value={intl.get("entry.ixus.textFields.climbingSquare")} />
                                {unplacedLevels.map(level => (
                                    <Button
                                        key={"level-" + level.planKey}
                                        onClick={() => armSafetyDistances(level)}
                                        value={selectedIxusArea?.sideView
                                            ? intl.get("entry.ixus.textFields.safetyDistancesLevel", { level: level.label })
                                            : intl.get("entry.ixus.textFields.safetyDistances")} />
                                ))}
                                {unplacedTexts.map((text, index) => (
                                    <Button
                                        key={index}
                                        onClick={() => (text === directionalRadioText ? armDirectionalRadio() : armText(text))}
                                        value={shortenButtonLabel(text)} />
                                ))}
                            </>
                        }
                    </Grid>
                </>
            }
            {freeTextOpen &&
                <IxusFreeTextDialog
                    onConfirm={text => {
                        setFreeTextOpen(false);
                        armText(text);
                    }}
                    onClose={() => setFreeTextOpen(false)} />
            }
        </LayerStyle>
    );
};
